import { ReactElement } from 'react';

import {
   MedicalInformation,
   MedicalInformationPractitioner,
} from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { Modal, ModalCancelButton, ModalCloseButton, ModalWidth, StyledFlex, TextView } from '@dreryk/react-components';
import { Translate } from '@dreryk/react-common';

import { protectFeature } from '~libs.shared.system';
import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';

import {
   medicalDocumentationCreateTranslationKeys,
   patientMedicalInformationTranslationKeys,
} from '../../translations';
import { dispatchRefetchUnsignedMedicalInformationAttachmentCountEvent } from '../RefetchUnsignedMedicalInformationAttachmentCount.events';

import { MedicalDataAccessAuthorizationUpdateButton } from './MedicalDataAccessAuthorizationUpdateButton';
import { MedicalInformationClosePreviewModalButton } from './MedicalInformationClosePreviewModalButton';
import { MedicalInformationDescriptionTextView } from './MedicalInformationDescriptionTextView';
import { MedicalInformationAttachmentsTable } from './MedicalInformationAttachmentsTable';
import { StyledStatusBadge } from './MedicalInformationModal.styled';

const showPersonSigningAttachments = (
   personEnteringEntry: MedicalInformationPractitioner | undefined,
   personSigningAttachments: MedicalInformationPractitioner | undefined
): personSigningAttachments is MedicalInformationPractitioner => {
   return Boolean(
      personEnteringEntry &&
         personSigningAttachments &&
         personSigningAttachments.id.asNumber() !== personEnteringEntry.id.asNumber()
   );
};

export type MedicalInformationPreview = Omit<
   MedicalInformation,
   'createdBy' | 'creationDate' | 'confidential' | 'hasAccess' | 'pendingAccessRequest'
> & {
   readonly createdBy?: MedicalInformationPractitioner;
   readonly creationDate?: Date;
};

type MedicalInformationModalProps = {
   readonly medicalInformation: MedicalInformationPreview;
   readonly onClose: VoidFunction;
   readonly onMedicalDataAccessAuthorizationUpdate?: VoidFunction;
   readonly children?: ReactElement;
};

// eslint-disable-next-line max-lines-per-function
export const MedicalInformationModal = (props: MedicalInformationModalProps): JSX.Element => {
   return (
      <Modal
         title={
            <StyledFlex gap={8}>
               <Translate>
                  {medicalDocumentationCreateTranslationKeys.categories[props.medicalInformation.type]}
               </Translate>
               {props.medicalInformation.medicalInformationRemoved ? (
                  <StyledStatusBadge testId={'medicalInformationStatusBadge'} type={'unapproved'}>
                     <Translate>{medicalDocumentationCreateTranslationKeys.removed}</Translate>
                  </StyledStatusBadge>
               ) : null}
            </StyledFlex>
         }
         onCloseRequest={props.onClose}
         width={ModalWidth.NORMAL}
         testId={'medicalInformation'}
      >
         <StyledFlex direction={'column'} gap={'24px'}>
            {props.medicalInformation.createdBy ? (
               <TextView
                  label={patientMedicalInformationTranslationKeys.previewModal.personEnteringEntry}
                  testId={'personEntering'}
               >
                  <PractitionerSafePresenter
                     testId={'personEntering'}
                     practitioner={props.medicalInformation.createdBy}
                     type={'text'}
                     withIdentification
                  />
               </TextView>
            ) : null}
            {showPersonSigningAttachments(
               props.medicalInformation.createdBy,
               props.medicalInformation.attachmentsSignedBy
            ) && (
               <TextView
                  label={patientMedicalInformationTranslationKeys.previewModal.personSigningAttachments}
                  testId={'personSigning'}
               >
                  <PractitionerSafePresenter
                     testId={'personSigning'}
                     practitioner={props.medicalInformation.attachmentsSignedBy}
                     type={'text'}
                     withIdentification
                  />
               </TextView>
            )}
            {props.children}
            <MedicalInformationDescriptionTextView description={props.medicalInformation.description} />
            <MedicalInformationAttachmentsTable medicalInformation={props.medicalInformation} />
         </StyledFlex>

         <ModalCloseButton onClick={props.onClose} />

         {props.medicalInformation.unsignedAttachmentCount ? (
            protectFeature(
               'creatingMedicalDocuments',
               <>
                  <ModalCancelButton onClick={props.onClose} />
                  <MedicalDataAccessAuthorizationUpdateButton
                     medicalInformationId={props.medicalInformation.id}
                     onClose={props.onClose}
                     onMedicalDataAccessAuthorizationUpdate={() => {
                        props.onMedicalDataAccessAuthorizationUpdate?.();
                        dispatchRefetchUnsignedMedicalInformationAttachmentCountEvent({});
                     }}
                  />
               </>,
               <MedicalInformationClosePreviewModalButton onClose={props.onClose} />
            )
         ) : (
            <MedicalInformationClosePreviewModalButton onClose={props.onClose} />
         )}
      </Modal>
   );
};
